import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getTranslation } from "../../translations/getTranslations";
import { useNotification } from "../../notifications/Notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faPlus, faTrash, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import {
  assignGatewayToUser,
  getAllMembers,
  unassignGatewayFromUser,
  getAllOwnedGateways,
} from "../../services/gatewayService";
import { initializeUsers } from "../../redux/slices/usersSlice";

const UsersForm = ({ isMobile }) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState("");
  const [ownedGateways, setOwnedGateways] = useState([]);
  const [showSerialNumberDropdown, setShowSerialNumberDropdown] = useState(false);
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
  const panelDropdownRef = useRef(null);

  const users = useSelector(state => state.users.data);
  const language = useSelector(state => state.settings.language);
  const notify = useNotification();
  const dispatch = useDispatch();

  const translation = key => getTranslation("usersForm", key, language);

  // Fetch members
  const fetchGateways = useCallback(async () => {
    try {
      const members = await getAllMembers();
      dispatch(initializeUsers(members));
    } catch (error) {
      const errorKey = error.message?.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorKey));
    }
    // eslint-disable-next-line
  }, [dispatch]);

  const fetchOwnedGateways = useCallback(async () => {
    const data = await getAllOwnedGateways();
    setOwnedGateways(data);
  }, []);

  useEffect(() => {
    fetchGateways();
    fetchOwnedGateways();
  }, [fetchGateways, fetchOwnedGateways]);

  const handleToggleDropdown = () => setShowSerialNumberDropdown(prev => !prev);

  const handleCheckboxToggle = sn => {
    setSelectedSerialNumbers(prev => (prev.includes(sn) ? prev.filter(item => item !== sn) : [...prev, sn]));
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (panelDropdownRef.current && !panelDropdownRef.current.contains(event.target)) {
        setShowSerialNumberDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInviteClick = () => {
    setShowInviteForm(prev => !prev);
    setInvitationEmail("");
    setSelectedSerialNumbers([]);
  };

  const handleInviteUser = async () => {
    if (!invitationEmail || selectedSerialNumbers.length === 0) {
      notify("warn", translation("_fillFieldsAlert"));
      return;
    }

    try {
      await assignGatewayToUser(invitationEmail, selectedSerialNumbers);
      notify("success", `${translation("_inviteSent")} ${invitationEmail}`);
      fetchGateways();
      setSelectedSerialNumbers([]);
      setInvitationEmail("");
      setShowInviteForm(false);
    } catch (error) {
      notify("error", translation("_inviteError"));
    }
  };

  const handleDeleteUser = async index => {
    const user = users[index];
    const { userId, olySerialNumber } = user;
    try {
      await unassignGatewayFromUser(olySerialNumber, userId);
      notify("success", translation("_deleteUserSuccess"));
      fetchGateways();
    } catch (error) {
      console.error("Error unassigning user:", error);
      notify("error", translation("_deleteUserError"));
    }
  };

  return (
    <div className="text-lg">
      {/* Header */}
      <div className="mb-2 flex items-center text-2xl font-semibold">
        <FontAwesomeIcon icon={faUser} className="mr-2" /> {translation("_users")}
      </div>
      <p>{translation("_usersDescription")}</p>

      {/* Invite Form */}
      <div className="flex flex-col gap-2">
        {!showInviteForm && (
          <button
            className="h-[40px] w-[150px] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover"
            onClick={handleInviteClick}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {translation("_inviteUser")}
          </button>
        )}

        {showInviteForm && (
          <div className="relative flex flex-col gap-3 rounded border bg-inputBackground p-4 shadow-md">
            <div className={`flex flex-col gap-3 ${isMobile ? "items-center" : "xl:flex-row"}`}>
              {/* Email input */}
              <input
                className={`w-[350px] ${isMobile ? "w-full max-w-[300px]" : ""} rounded border border-inputBorder bg-inputBackground px-2 py-1 text-textPrimary hover:bg-inputHover focus:outline-none`}
                type="email"
                name="email"
                placeholder={translation("_emailPlaceholder")}
                value={invitationEmail}
                onChange={e => setInvitationEmail(e.target.value)}
              />

              {/* Dropdown */}
              <div ref={panelDropdownRef} className={`relative w-[350px] ${isMobile ? "w-full max-w-[300px]" : ""}`}>
                <button
                  onClick={handleToggleDropdown}
                  className="flex w-full items-center justify-between rounded border bg-inputBackground p-2 hover:bg-inputHover"
                >
                  {/* Scrollable selected list */}
                  <div className="scrollbar-hide flex-1 overflow-x-auto whitespace-nowrap">
                    {selectedSerialNumbers.length > 0
                      ? selectedSerialNumbers.join(", ")
                      : translation("_selectGateway")}
                  </div>
                  <FontAwesomeIcon icon={showSerialNumberDropdown ? faChevronDown : faChevronRight} />
                </button>

                {showSerialNumberDropdown && (
                  <div className="absolute z-50 mt-1 max-h-[150px] w-full overflow-auto rounded border bg-inputBackground text-textPrimary shadow-lg">
                    {ownedGateways.map((gateway, index) => (
                      <label key={index} className="flex cursor-pointer items-center gap-2 p-2 hover:bg-inputHover">
                        <input
                          type="checkbox"
                          checked={selectedSerialNumbers.includes(gateway.olySerialNumber)}
                          onChange={() => handleCheckboxToggle(gateway.olySerialNumber)}
                        />
                        {gateway.name} ({gateway.olySerialNumber})
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className={`flex ${isMobile ? "flex-col items-center gap-2" : "items-center gap-2"}`}>
                <button
                  className="h-[40px] w-[120px] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover"
                  onClick={handleInviteUser}
                >
                  {translation("_sendInvite")}
                </button>
                <button
                  className="h-[40px] w-[120px] rounded bg-buttonCancel p-1 text-buttonText hover:bg-buttonCancelHover"
                  onClick={handleInviteClick}
                >
                  {translation("_cancel")}
                </button>
              </div>
            </div>

            {/* {errorMessage && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>} */}
          </div>
        )}
      </div>

      {/* Users Table */}
      {users && users.length > 0 && (
        <div className="mt-4 flex justify-center">
          <div className="w-[80%] overflow-hidden shadow-md">
            <div className="max-h-[250px] overflow-y-auto">
              <table className="w-full border-separate border-spacing-0 text-left">
                <thead className="sticky top-0 bg-tableHeaderBackground text-tableHeaderText">
                  <tr className="h-[50px] border-inputBorder">
                    <th className="w-[50%] rounded-tl p-2">{translation("_email")}</th>
                    <th className="w-[30%] p-2">{translation("_assignedGateway")}</th>
                    <th className="w-[20%] rounded-tr p-2 text-center">{translation("_actions")}</th>
                  </tr>
                </thead>
                <tbody className="bg-inputBackground text-textPrimary">
                  {users.map((user, index) => (
                    <tr key={index} className="h-[50px]">
                      <td className="w-[50%] p-2">{user.email}</td>
                      <td className="w-[30%] p-2">{user.gatewayName}</td>
                      <td className="w-[20%] p-2 text-center">
                        <button
                          className="text-buttonCancel hover:text-buttonCancelHover"
                          onClick={() => handleDeleteUser(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersForm;
